import React, { useState, useEffect } from 'react';
import {
  X,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  Info,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit3,
  ImageOff,
  ExternalLink,
} from 'lucide-react';
import { seoApi } from '../api/client';
import { roaryApi, isAltProposal } from '../api/modules/roary';
import type { SeoIssueProposal, SeoAltProposalItem } from '../api/modules/roary';
import type {
  SEOAnalysisResult,
  ImageIssueDetail,
  SEOIssue,
  ComponentType,
} from '../schemas/validation';
import { MarshmallowPacksModal } from './Roary/MarshmallowPacksModal';
import {
  CANNOT_AUTO_FIX_SUMMARY,
  THIN_CONTENT_COMPONENT_OPTIONS,
  isRoaryFixableIssue,
  isThinContentIssue,
} from '../utils/seoIssueFixable';
import roaryMascot from '/assets/Roary-mascot.png';

type FixNote = {
  kind: 'info' | 'error';
  text: string;
  reason?: 'marshmallows';
};

function isMarshmallowDepletedError(err: {
  status?: number;
  data?: { status?: number };
  message?: string;
}): boolean {
  const status = err.status || err.data?.status;
  return status === 402 || (err.message?.toLowerCase().includes('marshmallow') ?? false);
}

function currentPageUrl(): string {
  return `${window.location.origin}${window.location.pathname}${window.location.search}`;
}

interface SEOAnalysisPanelProps {
  // Flexible props - can work with pageId, siteId, or blog post
  pageId?: string;
  siteId?: string;
  blogId?: string;
  postId?: string;
  isOpen: boolean;
  onClose: () => void;
  title?: string; // Optional custom title
  onEditComponent?: (componentId: string) => void; // Callback to edit a component
  onFixApplied?: () => void; // Reload editor state after Roary writes a fix
  /** Add a template component (page editor). Used for the thin-content picker. */
  onAddComponent?: (componentType: ComponentType) => void | Promise<void>;
  /** Site-wide Social Image. Lets Roary Fix Open Graph when the page has no photos. */
  siteOgImage?: string;
}

export const SEOAnalysisPanel: React.FC<SEOAnalysisPanelProps> = ({
  pageId,
  blogId,
  postId,
  isOpen,
  onClose,
  title,
  onEditComponent,
  onFixApplied,
  onAddComponent,
  siteOgImage,
}) => {
  const [analysis, setAnalysis] = useState<SEOAnalysisResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(['overview', 'issues'])
  );
  const [showImageIssues, setShowImageIssues] = useState(false);
  const [fixingIndex, setFixingIndex] = useState<number | null>(null);
  const [applyingIndex, setApplyingIndex] = useState<number | null>(null);
  const [previews, setPreviews] = useState<Record<number, SeoIssueProposal>>({});
  const [editedValues, setEditedValues] = useState<Record<number, string>>({});
  const [editedAltItems, setEditedAltItems] = useState<Record<number, SeoAltProposalItem[]>>({});
  const [fixNotes, setFixNotes] = useState<Record<number, FixNote>>({});
  const [showMarshmallowPacks, setShowMarshmallowPacks] = useState(false);
  const [applyNotice, setApplyNotice] = useState<string | null>(null);
  const [thinContentPickerIndex, setThinContentPickerIndex] = useState<number | null>(null);
  const [addingThinContent, setAddingThinContent] = useState(false);
  const isBlogPost = Boolean(blogId && postId);

  // Determine which API to call based on props
  useEffect(() => {
    if (!isOpen) return;

    const fetchAnalysis = async () => {
      setIsLoading(true);
      setError(null);

      try {
        let result: SEOAnalysisResult;

        if (pageId) {
          result = await seoApi.getPageAnalysis(pageId);
        } else if (blogId && postId) {
          console.log('🔍 Making SEO API call:', {
            blogId,
            postId,
            url: `/seo/blogs/${blogId}/posts/${postId}/seo`,
          });

          result = await seoApi.getBlogPostAnalysis(blogId, postId);
        } else {
          throw new Error('Must provide either pageId or (blogId and postId)');
        }

        setAnalysis(result);
        setApplyNotice(null);
        setFixNotes({});
        setPreviews({});
        setEditedValues({});
        setEditedAltItems({});
        setThinContentPickerIndex(null);
      } catch (err: any) {
        setError(err.message || 'Failed to load SEO analysis');
        console.error('SEO analysis error:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchAnalysis();
  }, [isOpen, pageId, blogId, postId]);

  useEffect(() => {
    if (!isOpen) {
      setShowMarshmallowPacks(false);
    }
  }, [isOpen]);

  const handleRefresh = async () => {
    // Re-fetch analysis
    setIsLoading(true);
    setError(null);
    try {
      let result: SEOAnalysisResult;
      if (pageId) {
        result = await seoApi.getPageAnalysis(pageId);
      } else if (blogId && postId) {
        result = await seoApi.getBlogPostAnalysis(blogId, postId);
      } else {
        throw new Error('Must provide either pageId or (blogId and postId)');
      }
      setAnalysis(result);
      setApplyNotice(null);
      setFixNotes({});
      setPreviews({});
      setEditedValues({});
      setEditedAltItems({});
      setThinContentPickerIndex(null);
    } catch (err: any) {
      setError(err.message || 'Failed to refresh SEO analysis');
    } finally {
      setIsLoading(false);
    }
  };

  const clearPreview = (index: number) => {
    setPreviews(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setEditedValues(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
    setEditedAltItems(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const markIssueFixed = async (index: number) => {
    onFixApplied?.();
    clearPreview(index);
    setApplyNotice('Saved to the draft. Publish to make it live.');
    try {
      const fresh = pageId
        ? await seoApi.getPageAnalysis(pageId)
        : await seoApi.getBlogPostAnalysis(blogId!, postId!);
      setAnalysis(fresh);
      setFixNotes({});
      setPreviews({});
      setEditedValues({});
      setEditedAltItems({});
    } catch {
      // Keep the current issue list even if the refresh fails; the draft notice still shows.
    }
  };

  const issueFixMaterial = {
    siteOgImage,
    pageImageUrls: (analysis?.checks.images.issues ?? [])
      .map(img => img.imageUrl)
      .filter((url): url is string => Boolean(url)),
    imageIssueCount: analysis?.checks.images.issues?.length ?? 0,
  };

  const canFixIssue = (issue: SEOIssue, index: number) => {
    const bounced = fixNotes[index]?.text?.includes(CANNOT_AUTO_FIX_SUMMARY);
    if (isThinContentIssue(issue.message) && !onAddComponent) return false;
    return isRoaryFixableIssue(issue.message, isBlogPost, issueFixMaterial) && !bounced;
  };

  const handleFixIssue = async (issue: SEOIssue, index: number) => {
    if (!canFixIssue(issue, index)) return;
    if (fixingIndex !== null || applyingIndex !== null || previews[index]) return;

    if (isThinContentIssue(issue.message)) {
      setThinContentPickerIndex(index);
      return;
    }

    setThinContentPickerIndex(null);
    setFixingIndex(index);
    setFixNotes(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });

    try {
      const result = await roaryApi.fixSeoIssue({
        pageId,
        blogId,
        postId,
        issue: {
          type: issue.type,
          message: issue.message,
          recommendation: issue.recommendation,
        },
      });

      if (result.proposal) {
        const proposal = result.proposal;
        setPreviews(prev => ({ ...prev, [index]: proposal }));
        if (isAltProposal(proposal)) {
          setEditedAltItems(prev => ({
            ...prev,
            [index]: proposal.items.map(item => ({ ...item })),
          }));
        } else {
          setEditedValues(prev => ({ ...prev, [index]: proposal.proposed }));
        }
        setFixNotes(prev => ({
          ...prev,
          [index]: { kind: 'info', text: result.summary },
        }));
      } else {
        setFixNotes(prev => ({
          ...prev,
          [index]: { kind: 'info', text: result.summary },
        }));
      }
    } catch (err: any) {
      if (isMarshmallowDepletedError(err)) {
        setFixNotes(prev => ({
          ...prev,
          [index]: {
            kind: 'error',
            text: "You're out of marshmallows.",
            reason: 'marshmallows',
          },
        }));
      } else {
        setFixNotes(prev => ({
          ...prev,
          [index]: { kind: 'error', text: err.message || 'Roary could not fix this issue.' },
        }));
      }
    } finally {
      setFixingIndex(null);
    }
  };

  const handleAddThinContent = async (option: (typeof THIN_CONTENT_COMPONENT_OPTIONS)[number]) => {
    if (!onAddComponent || addingThinContent) return;

    setAddingThinContent(true);
    try {
      await onAddComponent(option.type);
      setThinContentPickerIndex(null);
      onFixApplied?.();
      setApplyNotice(`Added ${option.label} — you can edit it on the page.`);
      try {
        if (pageId) {
          setAnalysis(await seoApi.getPageAnalysis(pageId));
        }
      } catch {
        // Keep the current issue list if refresh fails.
      }
    } catch (err: any) {
      setFixNotes(prev => ({
        ...prev,
        [thinContentPickerIndex ?? 0]: {
          kind: 'error',
          text: err.message || `Failed to add ${option.label}.`,
        },
      }));
    } finally {
      setAddingThinContent(false);
    }
  };

  const handleApproveIssue = async (issue: SEOIssue, index: number) => {
    const proposal = previews[index];
    if (!proposal || applyingIndex !== null) return;

    setApplyingIndex(index);
    try {
      const result = isAltProposal(proposal)
        ? await roaryApi.applySeoIssue({
            pageId,
            blogId,
            postId,
            issue: {
              type: issue.type,
              message: issue.message,
              recommendation: issue.recommendation,
            },
            items: (editedAltItems[index] || proposal.items).map(item => ({
              componentId: item.componentId,
              itemIndex: item.itemIndex,
              proposed: item.proposed,
            })),
          })
        : await roaryApi.applySeoIssue({
            pageId,
            blogId,
            postId,
            issue: {
              type: issue.type,
              message: issue.message,
              recommendation: issue.recommendation,
            },
            value: editedValues[index] ?? proposal.proposed,
          });

      if (result.applied) {
        await markIssueFixed(index);
      } else {
        setFixNotes(prev => ({
          ...prev,
          [index]: { kind: 'error', text: result.summary || 'Roary could not save this change.' },
        }));
      }
    } catch (err: any) {
      setFixNotes(prev => ({
        ...prev,
        [index]: { kind: 'error', text: err.message || 'Roary could not save this change.' },
      }));
    } finally {
      setApplyingIndex(null);
    }
  };

  const handleDiscardIssue = (index: number) => {
    if (applyingIndex !== null) return;
    clearPreview(index);
    setFixNotes(prev => {
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBgColor = (score: number) => {
    if (score >= 80) return 'bg-green-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  const getIssueIcon = (type: string) => {
    switch (type) {
      case 'error':
        return <AlertCircle className="w-5 h-5 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'info':
        return <Info className="w-5 h-5 text-blue-500" />;
      default:
        return <Info className="w-5 h-5 text-gray-500" />;
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 z-[70] overflow-hidden">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />

        {/* Panel */}
        <div className="absolute right-0 top-0 h-full w-full max-w-2xl bg-white shadow-xl flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-semibold text-gray-900">
                {title || 'Improvements and SEO'}
              </h2>
              {analysis && (
                <div className={`px-3 py-1 rounded-full ${getScoreBgColor(analysis.score)}`}>
                  <span className={`text-sm font-bold ${getScoreColor(analysis.score)}`}>
                    {analysis.score}/100
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
                title="Refresh analysis"
              >
                <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
              </button>
              <button
                onClick={onClose}
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {isLoading && !analysis && (
              <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 text-gray-400 animate-spin" />
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  <p className="text-red-800">{error}</p>
                </div>
              </div>
            )}

            {analysis && (
              <div className="space-y-6">
                {/* Issues Section */}
                <Section
                  title="Issues"
                  count={analysis.issues.length}
                  isExpanded={expandedSections.has('issues')}
                  onToggle={() => toggleSection('issues')}
                >
                  {applyNotice && <p className="mb-3 text-sm text-green-700">{applyNotice}</p>}
                  {analysis.issues.length === 0 ? (
                    <div className="flex items-center space-x-2 text-green-600">
                      <CheckCircle2 className="w-5 h-5" />
                      <p>No issues found! Your page is SEO-optimized.</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {(
                        [
                          {
                            title: 'Roary can fix these',
                            items: analysis.issues
                              .map((issue, index) => ({ issue, index }))
                              .filter(({ issue, index }) => canFixIssue(issue, index)),
                          },
                          {
                            title: 'Other issues',
                            items: analysis.issues
                              .map((issue, index) => ({ issue, index }))
                              .filter(({ issue, index }) => !canFixIssue(issue, index)),
                          },
                        ] as const
                      ).map(group =>
                        group.items.length === 0 ? null : (
                          <div key={group.title} className="space-y-3">
                            <h4 className="text-sm font-semibold text-gray-800">{group.title}</h4>
                            <div className="space-y-3">
                              {group.items.map(({ issue, index }) => {
                                const isImageAltIssue =
                                  issue.message.toLowerCase().includes('missing alt') ||
                                  issue.message.toLowerCase().includes('default alt');
                                const hasImageIssues =
                                  analysis.checks.images.issues &&
                                  analysis.checks.images.issues.length > 0;
                                const isFixing = fixingIndex === index;
                                const isApplying = applyingIndex === index;
                                const preview = previews[index];
                                const fixNote = fixNotes[index];
                                const isPreviewing = !!preview;
                                const isPickingThinContent = thinContentPickerIndex === index;
                                const canRoaryFix = canFixIssue(issue, index);
                                const fixLabel = isPreviewing
                                  ? 'Review the proposed fix'
                                  : 'Ask Roary';

                                return (
                                  <div key={index}>
                                    <div
                                      className={`p-4 rounded-lg border ${
                                        issue.type === 'error'
                                          ? 'bg-red-50 border-red-200'
                                          : issue.type === 'warning'
                                            ? 'bg-yellow-50 border-yellow-200'
                                            : 'bg-blue-50 border-blue-200'
                                      }`}
                                    >
                                      <div className="flex items-start space-x-3">
                                        {getIssueIcon(issue.type)}
                                        <div className="flex-1 min-w-0">
                                          <div className="flex items-center flex-wrap gap-2">
                                            <p className="font-medium text-gray-900">
                                              {issue.message}
                                            </p>
                                          </div>
                                          {issue.recommendation &&
                                            !isPreviewing &&
                                            !isPickingThinContent && (
                                              <p className="mt-1 text-sm text-gray-600">
                                                💡 {issue.recommendation}
                                              </p>
                                            )}
                                          {isFixing && (
                                            <p className="mt-2 text-sm text-gray-500">
                                              Roary is working on this...
                                            </p>
                                          )}
                                          {fixNote && !isFixing && !isPreviewing && (
                                            <div className="mt-2 space-y-2">
                                              <p
                                                className={`text-sm ${
                                                  fixNote.kind === 'error'
                                                    ? 'text-red-600'
                                                    : 'text-gray-600'
                                                }`}
                                              >
                                                {fixNote.text}
                                              </p>
                                              {fixNote.reason === 'marshmallows' && (
                                                <button
                                                  type="button"
                                                  onClick={() => setShowMarshmallowPacks(true)}
                                                  className="px-3 py-1.5 text-xs font-semibold text-white bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors"
                                                >
                                                  Buy more marshmallows
                                                </button>
                                              )}
                                            </div>
                                          )}

                                          {isPickingThinContent && (
                                            <ThinContentPicker
                                              disabled={addingThinContent}
                                              onSelect={handleAddThinContent}
                                            />
                                          )}

                                          {isPreviewing && (
                                            <IssueFixPreview
                                              proposal={preview}
                                              summary={
                                                fixNote?.kind === 'info' ? fixNote.text : undefined
                                              }
                                              editedValue={
                                                !isAltProposal(preview)
                                                  ? (editedValues[index] ?? preview.proposed)
                                                  : ''
                                              }
                                              onEditedValueChange={value =>
                                                setEditedValues(prev => ({
                                                  ...prev,
                                                  [index]: value,
                                                }))
                                              }
                                              editedItems={
                                                isAltProposal(preview)
                                                  ? editedAltItems[index] || preview.items
                                                  : []
                                              }
                                              onEditedItemChange={(itemIndex, proposed) =>
                                                setEditedAltItems(prev => {
                                                  const items = [
                                                    ...(prev[index] ||
                                                      (isAltProposal(preview)
                                                        ? preview.items
                                                        : [])),
                                                  ];
                                                  if (!items[itemIndex]) return prev;
                                                  items[itemIndex] = {
                                                    ...items[itemIndex],
                                                    proposed,
                                                  };
                                                  return { ...prev, [index]: items };
                                                })
                                              }
                                              isApplying={isApplying}
                                              applyError={
                                                fixNote?.kind === 'error' ? fixNote.text : undefined
                                              }
                                              onApprove={() => handleApproveIssue(issue, index)}
                                              onDiscard={() => handleDiscardIssue(index)}
                                            />
                                          )}

                                          {/* Inline image issues for the missing alt text issue */}
                                          {isImageAltIssue && hasImageIssues && !isPreviewing && (
                                            <div className="mt-3 pt-3 border-t border-yellow-200">
                                              <button
                                                onClick={() => setShowImageIssues(!showImageIssues)}
                                                className="flex items-center space-x-1 text-sm text-yellow-700 hover:text-yellow-900 font-medium"
                                              >
                                                {showImageIssues ? (
                                                  <ChevronUp className="w-4 h-4" />
                                                ) : (
                                                  <ChevronDown className="w-4 h-4" />
                                                )}
                                                <span>
                                                  {showImageIssues ? 'Hide' : 'Show'}{' '}
                                                  {analysis.checks.images.issues!.length} image
                                                  {analysis.checks.images.issues!.length !== 1
                                                    ? 's'
                                                    : ''}{' '}
                                                  that need alt text
                                                </span>
                                              </button>

                                              {showImageIssues && (
                                                <ImageIssuesList
                                                  issues={analysis.checks.images.issues!}
                                                  onEditComponent={onEditComponent}
                                                />
                                              )}
                                            </div>
                                          )}
                                        </div>
                                        {canRoaryFix && (
                                          <button
                                            type="button"
                                            onClick={() => handleFixIssue(issue, index)}
                                            disabled={
                                              fixingIndex !== null ||
                                              applyingIndex !== null ||
                                              isPreviewing ||
                                              addingThinContent
                                            }
                                            className="flex-shrink-0 flex items-center gap-1 px-2 py-1 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-white/80 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                                            title={fixLabel}
                                            aria-label={fixLabel}
                                          >
                                            <img
                                              src={roaryMascot}
                                              alt=""
                                              className={`w-7 h-7 object-contain ${isFixing ? 'animate-pulse' : ''}`}
                                            />
                                            {!isPreviewing && !isPickingThinContent && (
                                              <span>Ask Roary</span>
                                            )}
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )
                      )}
                    </div>
                  )}
                </Section>

                {/* Detailed Checks */}
                <Section
                  title="Detailed Checks"
                  isExpanded={expandedSections.has('checks')}
                  onToggle={() => toggleSection('checks')}
                >
                  <div className="space-y-4">
                    {/* Meta Title */}
                    <CheckItem
                      label="Meta Title"
                      status={analysis.checks.metaTitle.status}
                      details={`${analysis.checks.metaTitle.length} characters`}
                      isGood={
                        analysis.checks.metaTitle.exists &&
                        analysis.checks.metaTitle.status === 'good'
                      }
                    />

                    {/* Meta Description */}
                    <CheckItem
                      label="Meta Description"
                      status={analysis.checks.metaDescription.status}
                      details={`${analysis.checks.metaDescription.length} characters`}
                      isGood={
                        analysis.checks.metaDescription.exists &&
                        analysis.checks.metaDescription.status === 'good'
                      }
                    />

                    {/* H1 Tags */}
                    <CheckItem
                      label="H1 Tags"
                      status={analysis.checks.h1Tags.status}
                      details={`${analysis.checks.h1Tags.count} found`}
                      isGood={analysis.checks.h1Tags.status === 'good'}
                    />

                    {/* Images */}
                    <CheckItem
                      label="Images"
                      status={analysis.checks.images.missingAlt > 0 ? 'warning' : 'good'}
                      details={`${analysis.checks.images.count} total, ${analysis.checks.images.missingAlt} missing alt text`}
                      isGood={analysis.checks.images.missingAlt === 0}
                    />

                    {/* Word Count */}
                    <CheckItem
                      label="Content"
                      status={analysis.checks.wordCount.status}
                      details={`${analysis.checks.wordCount.count} words`}
                      isGood={analysis.checks.wordCount.status === 'good'}
                    />

                    {/* Internal Links */}
                    <CheckItem
                      label="Internal Links"
                      status={analysis.checks.internalLinks.hasLinks ? 'good' : 'warning'}
                      details={`${analysis.checks.internalLinks.count} links`}
                      isGood={analysis.checks.internalLinks.hasLinks}
                    />

                    {/* Sitemap */}
                    <CheckItem
                      label="Sitemap"
                      status="good"
                      details="Automatically generated"
                      isGood={analysis.checks.sitemap.exists}
                      linkUrl={analysis.checks.sitemap.url}
                      linkLabel="View"
                    />

                    {/* Robots.txt */}
                    <CheckItem
                      label="Robots.txt"
                      status="good"
                      details="Automatically generated"
                      isGood={analysis.checks.robots.exists}
                      linkUrl={analysis.checks.robots.url}
                      linkLabel="View"
                    />

                    {/* SSL Certificate - Platform guaranteed */}
                    <CheckItem
                      label="SSL Certificate"
                      status="good"
                      details="Secure connection enabled"
                      isGood={true}
                    />
                  </div>
                </Section>
              </div>
            )}
          </div>
        </div>
      </div>
      <MarshmallowPacksModal
        isOpen={showMarshmallowPacks}
        onClose={() => setShowMarshmallowPacks(false)}
        successUrl={currentPageUrl()}
        cancelUrl={currentPageUrl()}
      />
    </>
  );
};

// Helper component for collapsible sections
interface SectionProps {
  title: string;
  count?: number;
  isExpanded: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ title, count, isExpanded, onToggle, children }) => {
  return (
    <div className="border border-gray-200 rounded-lg">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <h3 className="font-semibold text-gray-900">{title}</h3>
          {count !== undefined && (
            <span className="px-2 py-0.5 bg-gray-200 text-gray-700 text-xs rounded-full">
              {count}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-500" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-500" />
        )}
      </button>
      {isExpanded && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
};

// Helper component for check items
interface CheckItemProps {
  label: string;
  status: string;
  details: string;
  isGood: boolean;
  linkUrl?: string;
  linkLabel?: string;
}

const CheckItem: React.FC<CheckItemProps> = ({
  label,
  status,
  details,
  isGood,
  linkUrl,
  linkLabel,
}) => {
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
      <div className="flex items-center space-x-3">
        {isGood ? (
          <CheckCircle2 className="w-5 h-5 text-green-500" />
        ) : (
          <AlertCircle className="w-5 h-5 text-yellow-500" />
        )}
        <div>
          <p className="font-medium text-gray-900">{label}</p>
          <p className="text-sm text-gray-600">{details}</p>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {linkUrl && (
          <a
            href={linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
          >
            <span>{linkLabel || 'View'}</span>
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
        <span
          className={`px-2 py-1 text-xs rounded ${
            isGood ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}
        >
          {status}
        </span>
      </div>
    </div>
  );
};

// Preview of a proposed Roary SEO fix (before/after, editable, approve/discard)
function fieldCharLimits(field: string): { min: number; max: number } | null {
  if (field === 'title' || field === 'seoTitle') return { min: 30, max: 60 };
  if (field === 'description' || field === 'ogDescription' || field === 'seoDescription') {
    return { min: 120, max: 160 };
  }
  return null;
}

function isImageUrlField(field: string): boolean {
  return field === 'ogImage' || field === 'seoImageUrl';
}

function CharCount({
  value,
  limits,
}: {
  value: string;
  limits: { min: number; max: number } | null;
}) {
  const length = value.length;
  if (!limits) {
    return <span className="text-xs text-gray-500">{length} characters</span>;
  }
  const inRange = length >= limits.min && length <= limits.max;
  return (
    <span className={`text-xs ${inRange ? 'text-green-600' : 'text-amber-600'}`}>
      {length} characters ({limits.min}–{limits.max} recommended)
    </span>
  );
}

function PreviewThumb({ url, label }: { url: string; label?: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div className="flex flex-col items-start gap-1">
      {label ? <span className="text-xs text-gray-500">{label}</span> : null}
      <div className="w-20 h-20 bg-gray-100 rounded overflow-hidden border border-gray-200">
        {url && !failed ? (
          <img
            src={url}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setFailed(true)}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <ImageOff className="w-6 h-6" />
          </div>
        )}
      </div>
    </div>
  );
}

function ThinContentPicker({
  disabled,
  onSelect,
}: {
  disabled: boolean;
  onSelect: (option: (typeof THIN_CONTENT_COMPONENT_OPTIONS)[number]) => void;
}) {
  return (
    <div className="mt-3 pt-3 border-t border-yellow-200 space-y-3">
      <p className="text-sm text-gray-700">
        Let's add some more content to your page! How about one of these?
      </p>
      <div className="flex flex-wrap gap-2">
        {THIN_CONTENT_COMPONENT_OPTIONS.map(option => (
          <button
            key={option.type}
            type="button"
            disabled={disabled}
            onClick={() => onSelect(option)}
            className="px-3 py-1.5 text-sm font-medium text-gray-800 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

interface IssueFixPreviewProps {
  proposal: SeoIssueProposal;
  summary?: string;
  editedValue: string;
  onEditedValueChange: (value: string) => void;
  editedItems: SeoAltProposalItem[];
  onEditedItemChange: (itemIndex: number, proposed: string) => void;
  isApplying: boolean;
  applyError?: string;
  onApprove: () => void;
  onDiscard: () => void;
}

const IssueFixPreview: React.FC<IssueFixPreviewProps> = ({
  proposal,
  summary,
  editedValue,
  onEditedValueChange,
  editedItems,
  onEditedItemChange,
  isApplying,
  applyError,
  onApprove,
  onDiscard,
}) => {
  if (isAltProposal(proposal)) {
    return (
      <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
        {summary && <p className="text-sm text-gray-600">{summary}</p>}
        <div className="space-y-3">
          {editedItems.map((item, itemIndex) => (
            <div
              key={`${item.componentId}-${item.itemIndex ?? itemIndex}`}
              className="p-3 bg-white/70 border border-gray-200 rounded-lg space-y-2"
            >
              <div className="flex items-start gap-3">
                <PreviewThumb url={item.imageUrl} />
                <div className="flex-1 min-w-0 space-y-2">
                  {item.itemLabel && (
                    <p className="text-sm font-medium text-gray-800">{item.itemLabel}</p>
                  )}
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">Current</label>
                    <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">
                      {item.current || '(empty)'}
                    </p>
                  </div>
                  <div>
                    <label
                      className="block text-xs font-medium text-gray-700 mb-1"
                      htmlFor={`roary-alt-${item.componentId}-${itemIndex}`}
                    >
                      Roary's fix
                    </label>
                    <textarea
                      id={`roary-alt-${item.componentId}-${itemIndex}`}
                      value={item.proposed}
                      onChange={e => onEditedItemChange(itemIndex, e.target.value)}
                      disabled={isApplying}
                      rows={2}
                      className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        {applyError && <p className="text-sm text-red-600">{applyError}</p>}
        {isApplying && <p className="text-sm text-gray-500">Saving...</p>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onApprove}
            disabled={isApplying}
            className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Approve
          </button>
          <button
            type="button"
            onClick={onDiscard}
            disabled={isApplying}
            className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Discard
          </button>
        </div>
      </div>
    );
  }

  const limits = fieldCharLimits(proposal.field);
  const showImageThumbs = isImageUrlField(proposal.field);
  const useTextarea = !!limits && limits.max > 80;

  return (
    <div className="mt-3 pt-3 border-t border-gray-200 space-y-3">
      {summary && <p className="text-sm text-gray-600">{summary}</p>}
      <div className="space-y-3">
        {showImageThumbs && (
          <div className="flex gap-4">
            <PreviewThumb url={proposal.current} label="Current" />
            <PreviewThumb url={editedValue} label="Roary's fix" />
          </div>
        )}
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Current</label>
          <p className="text-sm text-gray-600 whitespace-pre-wrap break-words">
            {proposal.current || '(empty)'}
          </p>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="roary-fix-value">
            Roary's fix
          </label>
          {useTextarea ? (
            <textarea
              id="roary-fix-value"
              value={editedValue}
              onChange={e => onEditedValueChange(e.target.value)}
              disabled={isApplying}
              rows={3}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
            />
          ) : (
            <input
              id="roary-fix-value"
              type="text"
              value={editedValue}
              onChange={e => onEditedValueChange(e.target.value)}
              disabled={isApplying}
              className="w-full text-sm border border-gray-300 rounded-md px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:bg-gray-50"
            />
          )}
          {!showImageThumbs && (
            <div className="mt-1">
              <CharCount value={editedValue} limits={limits} />
            </div>
          )}
        </div>
      </div>
      {applyError && <p className="text-sm text-red-600">{applyError}</p>}
      {isApplying && <p className="text-sm text-gray-500">Saving...</p>}
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onApprove}
          disabled={isApplying}
          className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Approve
        </button>
        <button
          type="button"
          onClick={onDiscard}
          disabled={isApplying}
          className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Discard
        </button>
      </div>
    </div>
  );
};

// Component for displaying detailed image issues with thumbnails
interface ImageIssuesListProps {
  issues: ImageIssueDetail[];
  onEditComponent?: (componentId: string) => void;
}

const ImageIssuesList: React.FC<ImageIssuesListProps> = ({ issues, onEditComponent }) => {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  const handleImageError = (imageUrl: string) => {
    setFailedImages(prev => new Set(prev).add(imageUrl));
  };

  const getProblemLabel = (problem: string) => {
    switch (problem) {
      case 'missing-alt':
        return 'Missing alt text';
      case 'default-alt':
        return 'Default alt text';
      case 'large-image':
        return 'Large file size';
      default:
        return problem;
    }
  };

  return (
    <div className="mt-3 space-y-2">
      {issues.map((issue, index) => (
        <div
          key={`${issue.componentId}-${issue.itemIndex ?? index}`}
          className="flex items-center gap-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg"
        >
          {/* Thumbnail */}
          <div className="flex-shrink-0 w-16 h-16 bg-gray-100 rounded overflow-hidden">
            {issue.imageUrl && !failedImages.has(issue.imageUrl) ? (
              <img
                src={issue.imageUrl}
                alt=""
                className="w-full h-full object-cover"
                onError={() => handleImageError(issue.imageUrl)}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <ImageOff className="w-6 h-6" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <p className="font-medium text-gray-900 text-sm truncate">
              {issue.itemLabel || issue.componentLabel}
            </p>
            <p className="text-xs text-gray-500">
              {issue.componentLabel}
              {issue.itemIndex !== undefined && ` • Item ${issue.itemIndex + 1}`}
            </p>
            <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded">
              {getProblemLabel(issue.problem)}
            </span>
          </div>

          {/* Edit Button */}
          {onEditComponent && issue.componentId && (
            <button
              onClick={() => onEditComponent(issue.componentId)}
              className="flex-shrink-0 flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors"
              title="Edit this component to add alt text"
              aria-label="Edit this component to add alt text"
            >
              <Edit3 className="w-4 h-4" />
              <span>Edit</span>
            </button>
          )}
        </div>
      ))}
    </div>
  );
};
